import React, { useRef, useState } from "react";
import {
  X, Upload, Loader2, FileSpreadsheet, AlertCircle, CheckCircle,
  RefreshCw, Plus,
} from "lucide-react";
import { portalApi } from "../../lib/portalApi";

const STATUS_LABEL = {
  planned: "Not Started",
  in_progress: "In Progress",
  completed: "Completed",
  delayed: "Delayed",
  blocked: "Blocked / On Hold",
};

/**
 * MilestoneImportDialog — Admin / PM only.
 *
 * Two-step flow:
 *   1. Pick a .xlsx / .xls → POST /milestones/preview → server returns parsed
 *      rows with per-row validation (errors, warnings).
 *   2. Admin chooses replace-vs-append + visibility → POST /milestones/import.
 *
 * Rows with errors are visually flagged and EXCLUDED from the import — the
 * admin can re-upload a corrected file. We do not try to silently fix bad
 * rows or guess intent.
 */
export default function MilestoneImportDialog({ slug, onClose, onImported }) {
  const fileRef = useRef(null);
  const [phase, setPhase] = useState("pick"); // pick | parsing | preview | importing | done
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [visibility, setVisibility] = useState("client");
  const [result, setResult] = useState(null);

  const onPick = (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    setErr("");
    if (!/\.(xlsx|xls)$/i.test(f.name)) {
      setErr("Please choose an .xlsx or .xls Excel file.");
      return;
    }
    setFile(f);
    runPreview(f);
  };

  const runPreview = async (f) => {
    setPhase("parsing"); setErr(""); setPreview(null);
    try {
      const data = await portalApi.previewMilestonesUpload(slug, f);
      setPreview(data);
      setPhase("preview");
    } catch (ex) {
      setErr(ex?.message || "Could not parse the file.");
      setPhase("pick");
    }
  };

  const reset = () => {
    setFile(null); setPreview(null); setErr("");
    setPhase("pick"); setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const doImport = async () => {
    if (!preview) return;
    const validRows = (preview.rows || []).filter((r) => r.valid);
    if (!validRows.length) {
      setErr("No valid rows to import. Please fix the errors and re-upload.");
      return;
    }
    setPhase("importing"); setErr("");
    try {
      const payload = {
        rows: validRows.map((r) => ({
          name: r.name,
          stage: r.stage,
          description: r.description,
          planned_start: r.planned_start,
          planned_end: r.planned_end,
          status: r.status || "planned",
          progress_percent: r.progress_percent ?? 0,
          assignee: r.assignee,
          remarks: r.remarks,
        })),
        replace_existing: replaceExisting,
        visibility,
      };
      const res = await portalApi.importMilestones(slug, payload);
      setResult(res);
      setPhase("done");
      onImported && onImported(res);
    } catch (ex) {
      setErr(ex?.message || "Import failed.");
      setPhase("preview");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto"
      data-testid="portal-milestone-import-modal"
    >
      <div className="bg-[#FAF5EC] border border-[#6E1F2C]/20 w-full max-w-4xl my-8 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#6E1F2C]/15 bg-white">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C]">Milestones</p>
            <h2 className="font-serif-display text-xl text-[#1C110C]">Import from Excel</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="portal-milestone-import-close"
            className="text-[#7A6B5F] hover:text-[#6E1F2C] p-1"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Pick / Re-pick */}
        {phase === "pick" && <PickStep fileRef={fileRef} onPick={onPick} err={err} />}
        {phase === "parsing" && (
          <div className="px-6 py-16 text-center text-[#3E2A22]">
            <Loader2 size={28} className="mx-auto animate-spin text-[#6E1F2C] mb-3" />
            <p className="text-sm">Parsing {file?.name}…</p>
          </div>
        )}

        {/* Preview */}
        {phase === "preview" && preview && (
          <PreviewStep
            preview={preview}
            file={file}
            err={err}
            replaceExisting={replaceExisting}
            setReplaceExisting={setReplaceExisting}
            visibility={visibility}
            setVisibility={setVisibility}
            onConfirm={doImport}
            onChooseAnother={reset}
          />
        )}

        {phase === "importing" && (
          <div className="px-6 py-16 text-center text-[#3E2A22]">
            <Loader2 size={28} className="mx-auto animate-spin text-[#6E1F2C] mb-3" />
            <p className="text-sm">Importing milestones…</p>
          </div>
        )}

        {/* Done */}
        {phase === "done" && result && (
          <DoneStep result={result} onClose={onClose} onAnother={reset} />
        )}
      </div>
    </div>
  );
}

function PickStep({ fileRef, onPick, err }) {
  return (
    <div className="px-6 py-8">
      <div
        className="border-2 border-dashed border-[#6E1F2C]/35 bg-white px-6 py-10 text-center cursor-pointer hover:border-[#6E1F2C] transition-colors"
        onClick={() => fileRef.current?.click()}
        data-testid="portal-milestone-import-dropzone"
      >
        <FileSpreadsheet size={32} className="mx-auto text-[#6E1F2C] mb-3" />
        <p className="text-sm text-[#1C110C]">Click to choose a milestone Excel file</p>
        <p className="text-xs text-[#7A6B5F] mt-1">.xlsx or .xls — first sheet with a milestone-name column is auto-detected</p>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          className="hidden"
          onChange={onPick}
          data-testid="portal-milestone-import-file"
        />
      </div>
      <div className="mt-6 bg-white border border-[#6E1F2C]/15 px-5 py-4 text-xs text-[#3E2A22] space-y-2">
        <p className="font-medium text-[11px] tracking-[0.22em] uppercase text-[#6E1F2C]">Expected columns (any naming)</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Milestone name</strong> (required) — also accepts &quot;Task&quot;, &quot;Activity&quot;, &quot;Deliverable&quot;</li>
          <li><strong>Stage / Section</strong> — also &quot;Phase&quot;, &quot;Group&quot;, &quot;Package&quot;</li>
          <li><strong>Planned Start / Planned End</strong> — any common date format (e.g. 2026-03-15, 15-Mar-2026)</li>
          <li><strong>Status</strong> — Not Started / In Progress / Completed / Delayed / On Hold</li>
          <li><strong>Progress %</strong> — 0-100 or 0-1 fraction (auto-detected)</li>
          <li><strong>Assigned Team / Vendor</strong>, <strong>Description</strong>, <strong>Remarks</strong> — optional</li>
        </ul>
      </div>
      {err && (
        <p className="text-xs text-[#A3141A] mt-4" data-testid="portal-milestone-import-error">{err}</p>
      )}
    </div>
  );
}

function PreviewStep({
  preview, file, err, replaceExisting, setReplaceExisting,
  visibility, setVisibility, onConfirm, onChooseAnother,
}) {
  const validCount = preview.summary?.valid_rows ?? 0;
  const invalidCount = preview.summary?.invalid_rows ?? 0;
  return (
    <div className="px-6 py-5 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-[#3E2A22]">
          <span className="font-medium text-[#1C110C]">{file?.name}</span>
          <span className="text-[#7A6B5F]"> · sheet &quot;{preview.sheet_name}&quot; · header row {preview.header_row}</span>
        </div>
        <button
          type="button"
          onClick={onChooseAnother}
          data-testid="portal-milestone-import-rechoose"
          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-[#6E1F2C] hover:text-[#56151F] border border-[#6E1F2C]/30 hover:border-[#6E1F2C] px-3 py-1.5"
        >
          <RefreshCw size={11} /> Choose another file
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <Pill label="Detected fields" value={(preview.detected_fields || []).length} />
        <Pill label="Valid rows" value={validCount} good={validCount > 0} data-testid="portal-milestone-import-valid-count" />
        <Pill label="Invalid rows" value={invalidCount} bad={invalidCount > 0} data-testid="portal-milestone-import-invalid-count" />
        <Pill label="Existing in project" value={preview.existing_milestone_count ?? 0} />
      </div>

      {preview.duplicates_against_existing?.length > 0 && (
        <div
          className="text-xs px-3 py-2 bg-white border border-[#A6822F]/30 text-[#3E2A22]"
          data-testid="portal-milestone-import-dupes"
        >
          <AlertCircle size={12} className="inline mr-1 text-[#A6822F]" />
          {preview.duplicates_against_existing.length} row(s) match an existing milestone name in this project: {preview.duplicates_against_existing.slice(0, 4).join(", ")}{preview.duplicates_against_existing.length > 4 ? "…" : ""}
        </div>
      )}

      <div className="overflow-x-auto border border-[#6E1F2C]/15 max-h-[40vh]">
        <table className="w-full text-xs">
          <thead className="bg-white sticky top-0 z-10">
            <tr className="text-left text-[10px] tracking-[0.18em] uppercase text-[#6E1F2C]">
              <th className="px-3 py-2">Row</th>
              <th className="px-3 py-2">Milestone</th>
              <th className="px-3 py-2">Stage</th>
              <th className="px-3 py-2">Start → End</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">%</th>
              <th className="px-3 py-2">Notes</th>
            </tr>
          </thead>
          <tbody data-testid="portal-milestone-import-preview-table">
            {(preview.rows || []).map((r) => (
              <tr
                key={r.row_number}
                className={`border-t border-[#6E1F2C]/10 ${r.valid ? "bg-[#FAF5EC]" : "bg-[#A3141A]/5"}`}
                data-testid={`portal-milestone-import-row-${r.row_number}`}
              >
                <td className="px-3 py-2 text-[#7A6B5F]">{r.row_number}</td>
                <td className="px-3 py-2 text-[#1C110C]">
                  {r.name || <span className="text-[#A3141A] italic">—missing—</span>}
                </td>
                <td className="px-3 py-2 text-[#3E2A22]">{r.stage || "—"}</td>
                <td className="px-3 py-2 text-[#3E2A22] whitespace-nowrap">
                  {r.planned_start || "—"} → {r.planned_end || "—"}
                </td>
                <td className="px-3 py-2 text-[#3E2A22]">{STATUS_LABEL[r.status] || r.status || "—"}</td>
                <td className="px-3 py-2 text-[#3E2A22]">{r.progress_percent ?? 0}%</td>
                <td className="px-3 py-2 text-[#7A6B5F]">
                  {r.errors?.length > 0 && (
                    <span className="text-[#A3141A] block">⚠ {r.errors.join("; ")}</span>
                  )}
                  {r.warnings?.length > 0 && (
                    <span className="text-[#A6822F] block">i {r.warnings.join("; ")}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-[#6E1F2C]/15 px-5 py-4 space-y-3 text-sm">
        <p className="text-[10px] tracking-[0.22em] uppercase text-[#6E1F2C] font-medium">Import options</p>
        <fieldset className="space-y-2">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio" name="replaceMode" checked={!replaceExisting}
              onChange={() => setReplaceExisting(false)}
              data-testid="portal-milestone-import-mode-append"
              className="mt-0.5"
            />
            <div>
              <p className="text-[#1C110C]">Append to existing milestones</p>
              <p className="text-xs text-[#7A6B5F]">Adds these rows without touching the {preview.existing_milestone_count ?? 0} milestone(s) already in this project.</p>
            </div>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio" name="replaceMode" checked={replaceExisting}
              onChange={() => setReplaceExisting(true)}
              data-testid="portal-milestone-import-mode-replace"
              className="mt-0.5"
            />
            <div>
              <p className="text-[#A3141A]">Replace existing milestones</p>
              <p className="text-xs text-[#7A6B5F]">Permanently deletes all {preview.existing_milestone_count ?? 0} existing milestone(s) for this project before importing.</p>
            </div>
          </label>
        </fieldset>
        <div className="flex items-center gap-2 pt-2 border-t border-[#6E1F2C]/10">
          <span className="text-[10px] tracking-[0.22em] uppercase text-[#6E1F2C]">Visibility</span>
          <select
            value={visibility}
            onChange={(e) => setVisibility(e.target.value)}
            data-testid="portal-milestone-import-visibility"
            className="bg-white border border-[#6E1F2C]/20 px-3 py-1.5 text-xs"
          >
            <option value="client">Client-visible</option>
            <option value="internal">Internal only</option>
            <option value="public">Public (everyone)</option>
          </select>
        </div>
      </div>

      {err && (
        <p className="text-xs text-[#A3141A]" data-testid="portal-milestone-import-confirm-error">{err}</p>
      )}

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#6E1F2C]/10">
        <button
          type="button"
          onClick={onChooseAnother}
          data-testid="portal-milestone-import-cancel"
          className="px-4 py-2 text-[11px] tracking-[0.26em] uppercase text-[#7A6B5F] hover:text-[#6E1F2C]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={validCount === 0}
          data-testid="portal-milestone-import-confirm"
          className="inline-flex items-center gap-2 bg-[#6E1F2C] hover:bg-[#56151F] text-[#FAF5EC] px-5 py-2 text-[11px] tracking-[0.26em] uppercase disabled:opacity-60"
        >
          <Upload size={13} />
          Import {validCount} milestone{validCount === 1 ? "" : "s"}
        </button>
      </div>
    </div>
  );
}

function DoneStep({ result, onClose, onAnother }) {
  return (
    <div className="px-6 py-12 text-center">
      <CheckCircle size={36} className="mx-auto text-[#3F6E2C] mb-3" />
      <p className="font-serif-display text-xl text-[#1C110C]">Imported {result.imported_count} milestone{result.imported_count === 1 ? "" : "s"}</p>
      {result.replaced_existing && (
        <p className="text-xs text-[#A3141A] mt-2">Existing milestones for this project were replaced.</p>
      )}
      <p className="text-xs text-[#7A6B5F] mt-2">Batch id: <code>{result.batch_id}</code></p>
      <div className="flex items-center justify-center gap-3 mt-6">
        <button
          type="button"
          onClick={onAnother}
          data-testid="portal-milestone-import-another"
          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C] border border-[#6E1F2C]/30 hover:border-[#6E1F2C] px-4 py-2"
        >
          <Plus size={11} /> Import another file
        </button>
        <button
          type="button"
          onClick={onClose}
          data-testid="portal-milestone-import-done"
          className="bg-[#6E1F2C] hover:bg-[#56151F] text-[#FAF5EC] px-5 py-2 text-[11px] tracking-[0.26em] uppercase"
        >
          Close
        </button>
      </div>
    </div>
  );
}

function Pill({ label, value, good, bad, ...rest }) {
  const tone =
    bad ? "border-[#A3141A]/40 text-[#A3141A]"
    : good ? "border-[#3F6E2C]/40 text-[#3F6E2C]"
    : "border-[#6E1F2C]/20 text-[#3E2A22]";
  return (
    <div className={`bg-white border ${tone} px-3 py-2`} {...rest}>
      <p className="text-[9px] tracking-[0.22em] uppercase text-[#6E1F2C]">{label}</p>
      <p className="text-base font-medium">{value}</p>
    </div>
  );
}
